import { Grid, IconButton, Typography } from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BaseButton from 'src/common/components/base-button';
import { MessageModal } from 'src/common/components/interactive-dialogue/components/interactive-dialog-content/interactive-dialog-content';
import {
  popEmeraldModal,
  pushEmeraldModal,
} from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { ICommandConfiguration } from 'src/common/interfaces/dtos/ICommandsGroup';
import { ILoginUser } from 'src/common/interfaces/dtos/ILoginUser';
import { IMessageModal, MessageModalSizeEnum } from 'src/common/interfaces/dtos/IMessageModal';
import { IShowPassword } from 'src/common/interfaces/dtos/IShowPassword';
import { CssTextField } from 'src/css-text-field/css-text-field';
import { ScannedDataHandler } from 'src/devices/scanner-operations';
import { manageLogin } from './login-manager';
import { styles } from './supervisor-approval-commands-styles';

type Props = ISupervisorApprovalState;

export const showCommandSupervisorApprovalDialog = (
  props: Props,
  modalId?: string,
  onDataScanned?: ScannedDataHandler,
): void => {
  pushEmeraldModal(<CommandSupervisorApprovalModal {...props} />, onDataScanned, modalId, true);
};

const CommandSupervisorApprovalModal: React.FC<Props> = ({
  command,
  resolve,
  reject,
  modalId,
  signOffState,
  insufficientPrivileges,
}: Props): JSX.Element => {
  const classes = styles();

  const [userId, setUserId] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<IShowPassword>({ displayPassword: false });
  const [showIcon, setShowIcon] = useState<boolean>(false);
  const [changeColor, setChangeColor] = useState<boolean>(false);
  const [modalErrorMessage, setMessage] = useState<string>('');
  const [errors, setErrors] = useState<ILoginUserErrors>({
    supervisorId: '',
    supervisorPassword: '',
  });

  const { t } = useTranslation();

  const handleClickShowPassword = (): void => {
    setShowPassword({
      ...showPassword,
      displayPassword: !showPassword.displayPassword,
    });
  };

  const resetState = (): void => {
    setUserId('');
    setPassword('');
    setErrors({ supervisorId: '', supervisorPassword: '' });
  };

  const onClose = (): void => {
    resetState();
    popEmeraldModal(modalId);
    reject();
  };

  const handleCloudLogin = () => {
    const userCreds: ILoginUser = { userName: userId, password: password };
    manageLogin(userCreds)
      .then(data => {
        resolve({ sessionId: data.sessionId, userName: userId, password: password });
      })
      .catch(error => {
        setShowIcon(true);
        setChangeColor(true);
        if (error.code === 'Unauthorized') {
          setMessage('IncorrectUserPassword');
        } else {
          setMessage('InsufficentPrivileges');
        }
      });
  };

  const onTextChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const id = event.target.id;
    const value = event.target.value;
    switch (id) {
      case 'user-id':
        setUserId(value);
        setErrors({ ...errors, supervisorId: '' });
        break;
      case 'password':
        setPassword(value);
        setErrors({ ...errors, supervisorPassword: '' });
        break;
    }
  };

  const subMessage = (
    <Grid container justifyContent='center'>
      <Grid item>
        <Typography className={classes.subMessage}>
          {signOffState
            ? t('CommandSupervisorApprovalSignedOffText')
            : t('CommandSupervisorApprovalText1')}
        </Typography>
        <Typography
          className={classes.subMessageCommand}
          data-testid='show-permissions-modal-command-name'
        >
          {t(command?.name || '')}.
        </Typography>
        <Typography>{signOffState ? '' : t('CommandSupervisorApprovalText2')}</Typography>
      </Grid>
    </Grid>
  );

  const marketplaceProps: IMessageModal = {
    size: MessageModalSizeEnum.FullScreen,
    message: signOffState
      ? t('SupervisorApprovalCaptionSignedOff')
      : t('SupervisorApprovalCaption'),
    subMessage: subMessage,
    hideButtons: true,
    type: EmeraldDialogTypes.None,
    onAccept: handleCloudLogin,
    onCancel: onClose,
    fullScreen: true,
  };

  useEffect(() => {
    if (insufficientPrivileges) {
      setMessage('InsufficentPrivileges');
    }
  }, [insufficientPrivileges]);

  return (
    <MessageModal
      dialogTestIdPrefix='supervisor-approval-dialog'
      dialogContentComponent={
        <>
          {modalErrorMessage && (
            <Grid container justifyContent='center'>
              <Grid
                item
                container
                justifyContent='flex-start'
                alignItems='center'
                className={classes.errorContainer}
                style={{
                  width: '450px',
                }}
              >
                <ReportProblemOutlinedIcon classes={{ root: `${classes.erroricon}` }} />
                <span className={classes.errorMessage} data-testid='FormErrorLabel'>
                  {t(modalErrorMessage)}
                </span>
              </Grid>
            </Grid>
          )}
          {
            <>
              {' '}
              <Grid item className={classes.inputtextItem}>
                <CssTextField
                  className={
                    changeColor ? `${classes.inputTextFieldError}` : `${classes.inputTextField}`
                  }
                  variant='standard'
                  inputProps={{
                    'data-testid': 'user-id',
                    className: 'global-keyboard',
                    'data-custombuttons': 'keyboardWithNextButton',
                    style: { textIndent: '1vh' },
                  }}
                  id='user-id'
                  label={t('AuthModalUserId')}
                  name='User ID'
                  autoComplete='off'
                  autoFocus={true}
                  error={!!errors.supervisorId}
                  helperText={errors.supervisorId}
                  value={userId}
                  onChange={onTextChange}
                />
              </Grid>
              <Grid item className={classes.inputtextItem}>
                <CssTextField
                  className={
                    changeColor ? `${classes.inputTextFieldError}` : `${classes.inputTextField}`
                  }
                  variant='standard'
                  inputProps={{
                    'data-testid': 'password',
                    className: 'global-keyboard',
                    'data-custombuttons': 'keyboardWithNextButton',
                    style: { textIndent: '1vh' },
                  }}
                  id='password'
                  label={t('AuthModalPassword')}
                  name='Password'
                  autoComplete='off'
                  error={!!errors.supervisorPassword}
                  helperText={errors.supervisorPassword}
                  type={showPassword.displayPassword ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: showIcon ? (
                      <IconButton
                        className={classes.showPasswordIcon}
                        edge='end'
                        aria-label='Toggle password visibility'
                        onClick={handleClickShowPassword}
                        tabIndex={-1}
                        data-testid='toggle-password-btn'
                        disabled={userId && password ? false : true}
                      >
                        {showPassword.displayPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    ) : null,
                  }}
                  value={password}
                  onChange={onTextChange}
                />
              </Grid>
              <Grid container justifyContent='center'>
                <Grid item className={classes.buttonContainer}>
                  <BaseButton
                    buttonType='white'
                    className={classes.closeButton}
                    onClick={onClose}
                    data-testid='cancel-button-supervisor-approval'
                  >
                    {t('Cancel')}
                  </BaseButton>
                </Grid>
                <Grid item className={classes.buttonContainer}>
                  <BaseButton
                    className={classes.approveButton}
                    onClick={handleCloudLogin}
                    data-testid='approve-button-supervisor-approval'
                    disabled={userId && password ? false : true}
                  >
                    {signOffState ? t('SignInView_SignIn') : t('Approve')}
                  </BaseButton>
                </Grid>
              </Grid>
            </>
          }
        </>
      }
      {...marketplaceProps}
    />
  );
};

interface ILoginUserErrors {
  supervisorId: string;
  supervisorPassword: string;
}

export interface ISupervisorApprovalState {
  command?: ICommandConfiguration;
  resolve: (user: ILoginUser) => void;
  reject: () => void;
  modalId?: string;
  insufficientPrivileges?: boolean;
  signOffState: boolean;
}

export default CommandSupervisorApprovalModal;
